/**
 * Filter `type` value used by the workplace rating-prompt Knock guide. Authors of
 * the guide in the Knock dashboard set this as the guide type so `useGuides`
 * matches it from `ReviewPromptGuide`. Other Knock surfaces (`banner`, `modal`)
 * are untouched.
 */
export const REVIEW_PROMPT_GUIDE_TYPE = "workplace-app-store-review-soft-prompt";

/**
 * Knock user attribute that holds the ISO timestamp of the last time the
 * rating prompt's native review sheet was *requested*. Read by the Knock
 * dashboard's `daysSinceLastReviewPromptedAt` targeting rule so the gap
 * between prompts is dashboard-tunable (D-04).
 *
 * Caveat: the Capacitor plugin resolves whether the OS actually displayed
 * the sheet or silently rate-limited it (Apple caps to 3 per app per 365d;
 * Play also throttles). We persist the timestamp on every request, so a
 * rate-limited prompt counts the same as a shown prompt for cooldown.
 */
export const KNOCK_LAST_REVIEW_PROMPTED_AT_ATTRIBUTE = "lastReviewPromptedAt";

/**
 * Knock user attribute carrying the workplace account's age in whole days,
 * derived from the workplace user's `createdAt`. Read by the Knock dashboard
 * to enforce a minimum-tenure floor before any prompt fires (so brand-new
 * signups aren't asked to rate an app they haven't used yet).
 *
 * Emitted once per `KnockGuidesConsumers` mount by `useEmitReviewPromptAttributes`.
 * Only ticks daily, so the once-per-mount cadence is tolerable.
 */
export const KNOCK_ACCOUNT_AGE_IN_DAYS_ATTRIBUTE = "accountAgeInDays";

/**
 * Knock user attribute holding the ISO timestamp of the user's first session
 * on a Capacitor-native build of the app (iOS or Android). Read by the Knock
 * dashboard's `daysSinceFirstMobileAppUseAt` targeting rule to enforce a
 * "soak time on the mobile app" floor before any rating prompt fires —
 * stricter than `accountAgeInDays`, which tracks Cognito tenure regardless of
 * surface.
 *
 * Written exactly once per user-account: we read the current value off the
 * `accountAgeInDays` identify response and only write the timestamp when it
 * is absent. This avoids a cross-device replay where a later mobile install
 * would otherwise overwrite the earliest one (Knock identify is last-write-
 * wins; there is no min-merge primitive). The Knock value becomes
 * "earliest mobile install we observed across any device."
 */
export const KNOCK_FIRST_MOBILE_APP_USE_AT_ATTRIBUTE = "firstMobileAppUseAt";
